/**
 * Client-side mirror of backend's `prevWindowFromRangeId` (in
 * routes/dashboard.routes.js). Lets the date-range pill render the resolved
 * comparison window inline — same dates the API will compute, without
 * waiting on a fetch.
 *
 *   resolvePrevRange('today', currentRange)        → { from, to, label }
 *   resolvePrevRange('this-month', currentRange)   → { from, to, label }
 *
 * Returns null for till-date (no comparison) and for ranges where the prev
 * window can't be derived. The label string matches backend output exactly.
 */

function ymd(d) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).format(d);
}

function shiftDays(yyyymmdd, delta) {
  const d = new Date(yyyymmdd + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

function shiftMonth(yyyymmdd, delta) {
  const [y, m] = yyyymmdd.split("-").map(Number);
  const total = y * 12 + (m - 1) + delta;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return `${ny}-${String(nm).padStart(2, "0")}-01`;
}

function lastDayOfMonth(yyyymmdd) {
  const next = shiftMonth(yyyymmdd, 1);
  const d = new Date(next + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

export function resolvePrevRange(rangeId, currentRange) {
  if (!currentRange || !currentRange.from || !currentRange.to) {
    // till-date / lifetime — no comparison.
    return null;
  }
  const fromIst = currentRange.from;
  const toIst   = currentRange.to;

  switch (rangeId) {
    case "today": {
      const y = shiftDays(toIst, -1);
      return { from: y, to: y, label: "vs yesterday" };
    }
    case "this-week":
      return {
        from:  shiftDays(fromIst, -7),
        to:    shiftDays(toIst,   -7),
        label: "vs last week",
      };
    case "this-month": {
      const thisFirst = fromIst.slice(0, 7) + "-01";
      const lastFirst = shiftMonth(thisFirst, -1);
      const dayOfMonth = Number(toIst.slice(8, 10));
      const lastMonthLastDay = Number(lastDayOfMonth(lastFirst).slice(8, 10));
      const cappedDay = Math.min(dayOfMonth, lastMonthLastDay);
      return {
        from:  lastFirst,
        to:    `${lastFirst.slice(0, 8)}${String(cappedDay).padStart(2, "0")}`,
        label: "vs last month",
      };
    }
    case "last-month": {
      const lastFirst = fromIst.slice(0, 7) + "-01";
      const prevFirst = shiftMonth(lastFirst, -1);
      return {
        from:  prevFirst,
        to:    lastDayOfMonth(prevFirst),
        label: "vs prev month",
      };
    }
    case "this-year": {
      const lastYear = Number(fromIst.slice(0, 4)) - 1;
      const md = toIst.slice(5);
      let prevTo = `${lastYear}-${md}`;
      if (md === "02-29") {
        const isLeap = (y) => (y % 4 === 0 && y % 100 !== 0) || (y % 400 === 0);
        if (!isLeap(lastYear)) prevTo = `${lastYear}-02-28`;
      }
      return {
        from:  `${lastYear}-01-01`,
        to:    prevTo,
        label: "vs last year",
      };
    }
    case "last-year": {
      const lastYear = Number(fromIst.slice(0, 4));
      const prevYear = lastYear - 1;
      return {
        from:  `${prevYear}-01-01`,
        to:    `${prevYear}-12-31`,
        label: "vs prev year",
      };
    }
    case "custom": {
      // Same-length window before the custom range.
      const fromMs = new Date(fromIst + "T00:00:00Z").getTime();
      const toMs   = new Date(toIst   + "T23:59:59Z").getTime();
      const lenMs  = toMs - fromMs;
      const pStart = new Date(fromMs - lenMs - 1000);
      const pEnd   = new Date(fromMs - 1000);
      return {
        from:  pStart.toISOString().slice(0, 10),
        to:    pEnd.toISOString().slice(0, 10),
        label: "vs prev period",
      };
    }
    case "till-date":
    default:
      return null;
  }
}

/** "01/05/26" — DD/MM/YY (Indian convention). */
export function fmtShortDate(yyyymmdd) {
  if (!yyyymmdd || typeof yyyymmdd !== "string" || yyyymmdd.length < 10) return null;
  const y = yyyymmdd.slice(2, 4);
  const m = yyyymmdd.slice(5, 7);
  const d = yyyymmdd.slice(8, 10);
  return `${d}/${m}/${y}`;
}

/** "01/05/26" or "01/05/26 → 14/05/26". */
export function fmtDateRange(fromDate, toDate) {
  const f = fmtShortDate(fromDate);
  const t = fmtShortDate(toDate);
  if (!f && !t) return null;
  if (!f || !t || f === t) return f || t;
  return `${f} → ${t}`;
}
